<template>
  <div class="user-progress-tracker">
    <!-- Filters -->
    <div class="flex flex-wrap gap-4 mb-5 items-center">
      <v-text-field
        v-model="searchQuery"
        prepend-inner-icon="mdi-magnify"
        label="Search by user or course"
        outlined
        dense
        hide-details
        class="max-w-xs"
        clearable
      />
      <v-select
        v-model="filterCourse"
        :items="courseOptions"
        label="Filter by Course"
        outlined
        dense
        hide-details
        clearable
        class="max-w-xs"
      />
      <v-select
        v-model="filterDepartment"
        :items="departmentOptions"
        label="Filter by Department"
        outlined
        dense
        hide-details
        clearable
        class="max-w-xs"
      />
      <v-select
        v-model="filterCertificate"
        :items="certificateOptions"
        label="Filter by Certificate"
        outlined
        dense
        hide-details
        clearable
        class="max-w-xs"
      />
      <v-spacer />
      <v-chip color="#21427d" dark small class="font-semibold">
        <v-icon left small>mdi-account-group</v-icon>
        {{ filteredRows.length }} Records
      </v-chip>
    </div>

    <!-- Data Table -->
    <v-data-table
      :headers="headers"
      :items="filteredRows"
      :items-per-page="10"
      class="progress-table elevation-1 rounded-lg"
      :footer-props="{ 'items-per-page-options': [5, 10, 25, 50] }"
      disable-sort
    >
      <!-- User column -->
      <template v-slot:item.userName="{ item }">
        <div class="flex items-center gap-3 py-2 px-4">
          <v-avatar size="34" color="#21427d">
            <span class="white--text font-bold text-sm">{{ getInitials(item.userName) }}</span>
          </v-avatar>
          <div>
            <div class="font-semibold text-sm">{{ item.userName }}</div>
            <div class="text-xs text-gray-500">{{ item.department }}</div>
          </div>
        </div>
      </template>

      <!-- Course column -->
      <template v-slot:item.courseTitle="{ item }">
        <div class="font-medium text-sm">{{ item.courseTitle }}</div>
      </template>

      <!-- Progress column (combining percent and lessons count) -->
      <template v-slot:item.progress="{ item }">
        <div class="py-1">
          <div class="font-semibold text-sm mb-0.5 text-gray-800">{{ item.progress }}%</div>
          <div class="text-xs text-gray-500 flex align-center gap-1">
            <v-icon x-small color="grey lighten-1">mdi-book-check-outline</v-icon>
            {{ item.completedLessons }} / {{ item.totalLessons }} Lessons
          </div>
        </div>
      </template>

      <!-- Assigned By -->
      <template v-slot:item.assignedBy="{ item }">
        <span class="text-sm font-medium text-gray-700">{{ item.assignedBy }}</span>
      </template>

      <!-- Enrolled On -->
      <template v-slot:item.enrolledOn="{ item }">
        <span class="text-sm text-gray-600" v-if="item.enrolledOn">
          {{ formatDate(item.enrolledOn) }}
        </span>
        <span class="text-sm text-gray-400 italic" v-else>--</span>
      </template>

      <!-- Deadline -->
      <template v-slot:item.deadline="{ item }">
        <span class="text-sm text-gray-600 font-semibold" v-if="item.deadline">
          {{ formatDate(item.deadline) }}
        </span>
        <span class="text-sm text-gray-400 italic" v-else>--</span>
      </template>

      <!-- Completed On -->
      <template v-slot:item.completedOn="{ item }">
        <span class="text-sm text-success font-semibold" v-if="item.completedOn">
          {{ formatDate(item.completedOn) }}
        </span>
        <span class="text-sm text-gray-400 italic" v-else>--</span>
      </template>

      <!-- Certificate column -->
      <template v-slot:item.certificate="{ item }">
        <v-btn
          v-if="item.certificateIssued && item.enrollmentId"
          small
          color="success"
          outlined
          class="normal-case rounded-lg font-semibold"
          @click="downloadUserCertificate(item)"
        >
          <v-icon left small>mdi-file-certificate</v-icon>Download
        </v-btn>
        <span class="text-sm text-gray-400 italic" v-else>--</span>
      </template>

      <!-- Status column -->
      <template v-slot:item.status="{ item }">
        <v-chip x-small :color="getStatusColor(item)" dark class="font-semibold">
          {{ getStatusLabel(item) }}
        </v-chip>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { state, getUserProgressTable } from './learningData'

export default {
  name: 'UserProgressTracker',
  data() {
    return {
      searchQuery: '',
      filterCourse: null,
      filterDepartment: null,
      filterCertificate: null,
      headers: [
        { text: 'User', value: 'userName', sortable: true },
        { text: 'Course', value: 'courseTitle', sortable: true },
        { text: 'Progress', value: 'progress', sortable: true },
        { text: 'Assigned By', value: 'assignedBy', sortable: true },
        { text: 'Enrolled On', value: 'enrolledOn', sortable: true },
        { text: 'Deadline', value: 'deadline', sortable: true },
        { text: 'Completed On', value: 'completedOn', sortable: true },
        { text: 'Certificate', value: 'certificate', sortable: false, align: 'center' },
        { text: 'Status', value: 'status', sortable: false, align: 'center' }
      ]
    }
  },
  computed: {
    allRows() {
      // Access state.progress and state.enrollments to track reactivity
      void state.progress.length
      void state.enrollments.length
      return getUserProgressTable().filter((r) => r.isEnrolled)
    },
    courseOptions() {
      return [...new Set(this.allRows.map((r) => r.courseTitle))]
    },
    departmentOptions() {
      return [...new Set(this.allRows.map((r) => r.department))]
    },
    certificateOptions() {
      const completedRows = this.allRows.filter((r) => r.progress === 100)
      return [...new Set(completedRows.map((r) => r.courseTitle))]
    },
    filteredRows() {
      let rows = this.allRows
      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase()
        rows = rows.filter(
          (r) =>
            r.userName.toLowerCase().includes(q) ||
            r.courseTitle.toLowerCase().includes(q) ||
            r.userEmail.toLowerCase().includes(q)
        )
      }
      if (this.filterCourse) {
        rows = rows.filter((r) => r.courseTitle === this.filterCourse)
      }
      if (this.filterDepartment) {
        rows = rows.filter((r) => r.department === this.filterDepartment)
      }
      if (this.filterCertificate) {
        rows = rows.filter((r) => r.courseTitle === this.filterCertificate && r.progress === 100)
      }
      return rows
    }
  },
  methods: {
    getInitials(name) {
      return name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .substring(0, 2)
        .toUpperCase()
    },

    getStatusLabel(item) {
      if (!item.isEnrolled) return 'Not Enrolled'
      const progress = item.progress
      if (progress >= 100) return 'Completed'
      if (progress >= 60) return 'On Track'
      if (progress > 0) return 'In Progress'
      return 'Not Started'
    },
    getStatusColor(item) {
      if (!item.isEnrolled) return '#757575'
      const progress = item.progress
      if (progress >= 100) return '#4caf50'
      if (progress >= 60) return '#2196f3'
      if (progress > 0) return '#ff9800'
      return '#9e9e9e'
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    },
    async downloadUserCertificate(item) {
      if (!item.enrollmentId) return
      try {
        const response = await this.$axios.get(`/learning_center/enrollments/${item.enrollmentId}/certificate`, {
          responseType: 'blob'
        })
        const blob = new Blob([response.data], { type: 'application/pdf' })
        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        link.download = `Certificate-${item.userName.replace(/\s+/g, '_')}-${item.courseTitle.replace(/\s+/g, '_')}.pdf`
        link.click()
        this.$toast.success('Certificate downloaded successfully!')
      } catch (err) {
        console.error(err)
        this.$toast.error('Failed to download certificate')
      }
    }
  }
}
</script>

<style scoped>
.progress-table {
  border: 1px solid #e0e0e0;
}
.progress-table >>> .v-data-table-header th {
  background: #f5f7fa !important;
  font-weight: var(--font-weight-semibold) !important;
  color: #21427d !important;
  font-size: var(--font-subheading-sm) !important;
  text-transform: none !important;
  letter-spacing: normal;
}
.progress-table >>> tbody tr:hover {
  background: #f0f4ff !important;
}
</style>
